import { useState, useEffect, useCallback, useMemo } from 'react';
import { stockAlertsService, type StockAlertItem } from '../services/stockAlertsService';
import { useAuth } from '../context/AuthContext';

interface UseMyStockAlertsResult {
  alerts: StockAlertItem[];
  loading: boolean;
  activeSkus: Set<string>;
  addAlert: (payload: { sku: string; title: string; brand: string; category: string; qtyWanted?: number }) => Promise<void>;
  removeAlert: (sku: string) => Promise<void>;
  updateQty: (sku: string, qtyWanted: number) => Promise<void>;
  isActive: (sku: string) => boolean;
  reload: () => void;
}

export function useMyStockAlerts(): UseMyStockAlertsResult {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<StockAlertItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!user) { setAlerts([]); return; }
    setLoading(true);
    stockAlertsService.list()
      .then(res => setAlerts(res.items))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  }, [user, tick]);

  const activeSkus = useMemo(
    () => new Set(alerts.filter(a => a.status === 'active').map(a => a.sku)),
    [alerts],
  );

  const addAlert = useCallback(async (payload: { sku: string; title: string; brand: string; category: string; qtyWanted?: number }) => {
    const item = await stockAlertsService.add(payload);
    setAlerts(prev => {
      const filtered = prev.filter(a => a.sku !== payload.sku);
      return [item, ...filtered];
    });
  }, []);

  const removeAlert = useCallback(async (sku: string) => {
    await stockAlertsService.remove(sku);
    setAlerts(prev => prev.filter(a => a.sku !== sku));
  }, []);

  const updateQty = useCallback(async (sku: string, qtyWanted: number) => {
    const updated = await stockAlertsService.updateQty(sku, qtyWanted);
    setAlerts(prev => prev.map(a => (a.sku === sku ? updated : a)));
  }, []);

  const isActive = useCallback((sku: string) => activeSkus.has(sku), [activeSkus]);
  const reload = useCallback(() => setTick(t => t + 1), []);

  return { alerts, loading, activeSkus, addAlert, removeAlert, updateQty, isActive, reload };
}
